<template>
    <div class="guided-substance-regulation-content">
        <div
            v-if="showSubstanceDeclaration"
            class="guided-regulations-review-section__declaration"
        >
            <h6 class="mb-50">
                {{ translate(
                    'Component(ResearchReviewProcess):substance-declaration',
                    'Substance declaration',
                ) }}
            </h6>
            <div class="guided-regulations-review-section__claim-options">
                <BFormRadio
                    v-for="option in substanceClaimOptions"
                    :key="option.value"
                    :checked="selectedSubstanceClaim"
                    :value="option.value"
                    :name="`guided-regulation-${regulationIndex}-substance-claim`"
                    class="guided-regulations-review-section__claim-option"
                    :class="{
                        'guided-regulations-review-section__claim-option--selected': selectedSubstanceClaim === option.value,
                    }"
                    @click.native="$emit('select-substance-claim', option.value)"
                >
                    <strong>{{ option.label }}</strong>
                </BFormRadio>
            </div>
            <BAlert v-if="hasAmbiguousSubstanceClaims" variant="light-warning" show class="mb-0 mt-75">
                <div class="alert-body">
                    {{ translate(
                        'Component(ResearchReviewProcess):select-one-substance-declaration',
                        'Select one substance declaration for this regulation.',
                    ) }}
                </div>
            </BAlert>
        </div>

        <div
            v-if="showActiveSubstancesSection"
            class="guided-regulations-review-section__substances"
        >
            <div class="guided-regulations-review-section__substances-heading">
                <div>
                    <h6 class="mb-25">
                        {{ translate('Component(ResearchReviewProcess):substances', 'Substances') }}
                    </h6>
                    <small class="text-less-muted">
                        {{ translate(
                            'Component(ResearchReviewProcess):regulation-substances-description',
                            'Review document values and their Chemity matches.',
                        ) }}
                    </small>
                    <div
                        v-if="!showSubstanceDeclaration"
                        class="guided-regulations-review-section__declaration-summary"
                    >
                        <small class="text-less-muted">
                            {{ translate(
                                'Component(ResearchReviewProcess):substance-declaration',
                                'Substance declaration',
                            ) }}:
                        </small>
                        <BBadge variant="light-primary">
                            {{ selectedSubstanceClaimLabel }}
                        </BBadge>
                        <BButton
                            type="button"
                            variant="flat-primary"
                            size="sm"
                            @click="$emit('edit-substance-declaration')"
                        >
                            {{ translate('Component(Button):change', 'Change') }}
                        </BButton>
                    </div>
                </div>
                <BButton
                    v-if="canAddSubstance"
                    type="button"
                    variant="outline-primary"
                    @click="$emit('add-substance')"
                >
                    <FeatherIcon icon="PlusIcon" size="13" class="mr-25" />
                    {{ translate('Component(ResearchReviewProcess):add-substance', 'Add substance') }}
                </BButton>
            </div>

            <BAlert
                v-if="selectedSubstanceClaim === 'does_not_contain_restricted_substances' && activeSubstanceItems.length"
                variant="light-warning"
                show
            >
                <div class="alert-body">
                    {{ translate(
                        'Component(ResearchReviewProcess):substance-declaration-conflict',
                        'This declaration says that restricted substances are not included, but linked substances are still associated with this regulation.',
                    ) }}
                </div>
            </BAlert>

            <BAlert v-if="!activeSubstanceItems.length" variant="light-secondary" show class="mb-0">
                <div class="alert-body">
                    {{ translate(
                        'Component(ResearchReviewProcess):no-regulation-substances',
                        'No matched substances are associated with this regulation.',
                    ) }}
                </div>
            </BAlert>

            <GuidedRegulationSubstanceRow
                v-for="item in activeSubstanceItems"
                ref="substanceRows"
                :key="`active-${item.rowKey}`"
                :row-key="item.rowKey"
                :substance-index="item.index"
                :substance="item.substance"
                :summary="item.summary"
                :matching="isHydrating(item)"
                :regulation-id="regulationId"
                :require-regulation-context="true"
                :default-concentration-basis="item.defaultConcentrationBasis"
                :violations="item.violations"
                @input="$emit('update-substance', item.index, $event)"
                @select-link="$emit('select-substance-link', item, $event)"
                @remove="$emit('remove-substance', item.index)"
            />

        </div>

        <GuidedRegulationExemptionsField
            v-if="showExemptions"
            :value="exemptionNames"
            :options="exemptionOptions"
            :supported="exemptionsSupported"
            @input="$emit('update-exemptions', $event)"
        />
    </div>
</template>

<script>
import {
    BAlert,
    BBadge,
    BButton,
    BFormRadio,
} from 'bootstrap-vue'
import GuidedRegulationExemptionsField from './GuidedRegulationExemptionsField.vue'
import GuidedRegulationSubstanceRow from './GuidedRegulationSubstanceRow.vue'

export default {
    name: 'GuidedSubstanceRegulationContent',
    components: {
        BAlert,
        BBadge,
        BButton,
        BFormRadio,
        GuidedRegulationExemptionsField,
        GuidedRegulationSubstanceRow,
    },
    props: {
        regulationIndex: {
            type: Number,
            default: null,
        },
        regulationId: {
            type: [Number, String],
            default: null,
        },
        showSubstanceDeclaration: {
            type: Boolean,
            required: true,
        },
        substanceClaimOptions: {
            type: Array,
            default: () => [],
        },
        selectedSubstanceClaim: {
            type: String,
            default: null,
        },
        hasAmbiguousSubstanceClaims: {
            type: Boolean,
            required: true,
        },
        showActiveSubstancesSection: {
            type: Boolean,
            required: true,
        },
        selectedSubstanceClaimLabel: {
            type: String,
            default: '',
        },
        canAddSubstance: {
            type: Boolean,
            required: true,
        },
        activeSubstanceItems: {
            type: Array,
            default: () => [],
        },
        hydratingIds: {
            type: Array,
            default: () => [],
        },
        showExemptions: {
            type: Boolean,
            default: false,
        },
        exemptionsSupported: {
            type: Boolean,
            default: false,
        },
        exemptionNames: {
            type: Array,
            default: () => [],
        },
        exemptionOptions: {
            type: Array,
            default: () => [],
        },
    },
    methods: {
        focusSubstanceViolation(index, field) {
            const rows = Array.isArray(this.$refs.substanceRows)
                ? this.$refs.substanceRows
                : [this.$refs.substanceRows].filter(Boolean)
            const row = rows.find(item => item.substanceIndex === index)

            return row && typeof row.focusViolation === 'function'
                ? row.focusViolation(field)
                : false
        },
        translate(key, fallback) {
            const translation = this.$t(key)

            return translation && translation !== key ? translation : fallback
        },
        isHydrating(item) {
            return !!(item.link && item.link.substanceId
                && this.hydratingIds.includes(Number(item.link.substanceId)))
        },
    },
}
</script>

<style lang="scss" scoped>
.guided-regulations-review-section__declaration {
    padding-bottom: 1rem;
}

.guided-regulations-review-section__claim-options {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: .75rem;
}

.guided-regulations-review-section__claim-option {
    min-height: 3rem;
    margin: 0;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
    cursor: pointer;
    transition: border-color .15s ease, background-color .15s ease;

    &.custom-radio.custom-control {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: .65rem 1rem;
    }

    &--selected {
        border-color: #f58049;
        background: rgba(245, 128, 73, .04);
    }

    ::v-deep .custom-control-label {
        display: inline-flex;
        align-items: center;
        width: auto;
        min-height: 1.3rem;
        padding-left: 2.5rem;
        cursor: pointer;
    }

    ::v-deep .custom-control-label::before,
    ::v-deep .custom-control-label::after {
        top: 50%;
        left: 0;
        margin-left: .75rem;
        transform: translateY(-50%);
    }
}

.guided-regulations-review-section__substances {
    padding-top: 0;
}

.guided-regulations-review-section__declaration + .guided-regulations-review-section__substances {
    padding-top: 1rem;
    border-top: 1px solid #ebe9f1;
}

.guided-regulations-review-section__substances-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: .75rem;
    margin-bottom: .75rem;
}

.guided-regulations-review-section__declaration-summary {
    display: flex;
    align-items: center;
    gap: .5rem;
    margin-top: .25rem;
}

@media (max-width: 767.98px) {
    .guided-regulations-review-section__claim-options {
        grid-template-columns: 1fr;
    }
}
</style>
